<script setup lang="ts">
import type { HouseAreaFormatted, HouseDetails } from '~/types/house/types'

const areaSelected = defineModel<string>()
const { house } = defineProps<{
  house: HouseDetails
  photosByArea: [string, HouseAreaFormatted][]
}>()

const route = useRoute()
const { locale } = useI18n()
const { isMobile } = useBreakpoint()
const { trackEvent } = useTracking()

const isAreaSelected = (hash: string) => {
  if (areaSelected.value) return areaSelected.value === hash
  if (route.hash) return route.hash === `#${hash}`
  return false
}

const handleClick = (hash: string) => {
  areaSelected.value = hash
  trackEvent({
    event: 'photo_grid_room_click',
    category: 'product page',
    cluster_id: house.destination.cluster?.clusterId ?? '',
    cluster_name: house.destination.cluster?.name ?? '',
    country_name: house.destination.country?.name ?? '',
    destination_id: house.destinationId ?? '',
    destination_name: house.destination.name ?? '',
    product: buildTrackEventProduct({
      houseId: house.id,
      houseName: house.name,
      iconic: Boolean(house.iconicCollection),
      onlineBookingAvailable: Boolean(house.onlineReservation),
    }),
    source: 'product page',
  })
  // Only way to natively scroll to hash + prevent a new history entry
  // - router.replace(...) / navigateTo({ hash: '#...'}, { replace: true })) does not scroll
  // - <a href="#..." adds an entry to the navigation history
  // - <NuxtLink :to="{ hash: '#...', replace: true }" does not scroll
  window.location.replace(`#${hash}`)
}
</script>

<template>
  <div class="flex gap-4 overflow-x-auto sm:flex-col sm:pb-10">
    <ClientOnly>
      <a
        v-for="[hash, area] in photosByArea"
        :key="hash"
        :aria-controls="hash"
        class="flex max-w-full cursor-pointer flex-col gap-2 text-xs text-gray-700 hover:no-underline sm:flex-row sm:items-center sm:gap-4 sm:text-md"
        @click.prevent="handleClick(hash)"
      >
        <div v-if="isMobile" class="h-28 w-40">
          <BaseNuxtImg
            class="rounded"
            height="112"
            width="160"
            :src="area.photoGallery.photos[0]?.url ?? ''"
          />
        </div>
        <template v-if="isAreaSelected(hash)">
          <span class="font-semibold sm:text-nowrap sm:font-medium">
            {{ area.name[locale] }}
          </span>
          <svg
            class="hidden grow stroke-gray-700 stroke-1 sm:inline"
            height="1"
            width="100%"
          >
            <line x1="0" y1="0" x2="100%" y2="0">
              <animate
                attributeName="x2"
                from="0"
                to="100%"
                dur="0.25s"
                repeatCount="1"
              />
            </line>
          </svg>
        </template>

        <span v-else class="font-semibold sm:text-nowrap sm:font-normal">
          {{ area.name[locale] }}
        </span>
      </a>

      <template #fallback>
        <div
          v-for="[hash] in photosByArea"
          :key="hash"
          class="h-28 w-full min-w-40 animate-pulse rounded bg-gray-300 sm:h-6 sm:rounded-full"
        />
      </template>
    </ClientOnly>
  </div>
</template>

<style scoped>
@supports (not (scrollbar-width: none)) {
  .overflow-x-auto::-webkit-scrollbar {
    @apply hidden;
  }
}
@supports (scrollbar-width: none) {
  .overflow-x-auto {
    scrollbar-width: none;
  }
}
</style>
